import { get } from 'lodash';

import { matchPath } from 'react-router';
import PermissionService from './PermissionService';
import { UserRoles } from '../constants/UserRole';

export default class ImpersonationService {
    static getImpersonation(user, userPrefs, origUserPrefs, location) {
        const permissions = PermissionService.getPermissions(user);
        const entityId = get(userPrefs, 'entityId', null);
        const employerRepId = get(origUserPrefs, 'employerRepId', null);
        const recruiterId = this.getRecruiterIdFromRoute(location);
        const employerId = this.getEmployerIdFromRoute(location);

        const options = {
            ...permissions,
            entityId,
            recruiterId,
            employerId,
            employerRepId,
        };

        if (permissions.isTalenyaUser) {
            return this.getImpersonationForTalenyaUser(options);
        }

        if (permissions.isTalentExpertUser) {
            return this.getImpersonationForTalentExpertUser(options);
        }

        return this.getImpersonationForEmployerUser(options);
    }

    static getImpersonationForTalenyaUser({
        entityId, recruiterId, employerId, isTalentExpertUser, isEmployerUser,
    }) {
        const isImpersonatedAsRecruiter = recruiterId && isTalentExpertUser;
        const isImpersonatedAsEmployer = (employerId && isEmployerUser)
            || (isTalentExpertUser && !recruiterId && employerId && !isEmployerUser);

        if (isImpersonatedAsRecruiter) {
            return {
                realEntityRole: UserRoles.talenya,
                realEntityId: entityId,
                impersonatedEntityRole: UserRoles.recruiter,
                impersonatedEntityId: recruiterId,
                impersonatedAsRecruiter: true,
                impersonatedAsEmployer: false,
            };
        }

        if (isImpersonatedAsEmployer) {
            return {
                realEntityRole: UserRoles.talenya,
                realEntityId: entityId,
                impersonatedEntityRole: UserRoles.employer,
                impersonatedEntityId: employerId,
                impersonatedAsRecruiter: false,
                impersonatedAsEmployer: true,
            };
        }

        return {
            realEntityRole: UserRoles.talenya,
            realEntityId: entityId,
            impersonatedEntityRole: null,
            impersonatedEntityId: null,
            impersonatedAsRecruiter: false,
            impersonatedAsEmployer: false,
        };
    }

    static getImpersonationForTalentExpertUser({ entityId, employerId }) {
        const isImpersonatedAsEmployer = employerId;

        if (isImpersonatedAsEmployer) {
            return {
                realEntityRole: UserRoles.recruiter,
                realEntityId: entityId,
                impersonatedEntityRole: UserRoles.employer,
                impersonatedEntityId: employerId,
                impersonatedAsRecruiter: false,
                impersonatedAsEmployer: true,
            };
        }

        return {
            realEntityRole: UserRoles.recruiter,
            realEntityId: entityId,
            impersonatedEntityRole: null,
            impersonatedEntityId: null,
            impersonatedAsRecruiter: false,
            impersonatedAsEmployer: false,
        };
    }

    static getImpersonationForEmployerUser({ employerRepId }) {
        return {
            realEntityRole: UserRoles.employerRep,
            realEntityId: employerRepId,
            impersonatedEntityRole: null,
            impersonatedEntityId: null,
            impersonatedAsRecruiter: false,
            impersonatedAsEmployer: false,
        };
    }

    static getRecruiterIdFromRoute(location) {
        const match = matchPath(location.pathname, {
            path: '/recruiter/:recruiterId',
            exact: false,
            strict: false,
        });

        return get(match, 'params.recruiterId', null);
    }

    static getPositionIdFromRoute(location) {
        const match = matchPath(location.pathname, {
            path: '/position/:positionId',
            exact: false,
            strict: false,
        });

        return get(match, 'params.positionId', null);
    }

    static getEmployerIdFromRoute(location) {
        const match = matchPath(location.pathname, {
            path: '/employer/:employerId',
            exact: false,
            strict: false,
        });

        return get(match, 'params.employerId', null);
    }
}

export const getEmployerRepId = (user = {}) => {
    const { impersonation = {} } = user;

    if (impersonation.impersonatedAsEmployer) {
        return null;
    }

    return impersonation.realEntityId;
};
